import {getLastInsertedOfflineInvoice, putInvoiceOffline, storeErrorToOffline} from "../store/offlineDb";
import {collection, getDocs, limit, orderBy, query, startAfter} from "firebase/firestore";
import {AUTH, DB} from "../auth/FirebaseContext";
import moment from "moment/moment";
import {nameOrDefault} from "./naming";
import {TICKET_TYPE_BASIC_NAME} from "../constants/other";
import {fCurrency} from "../utils/formatNumber";
import {OPERATORS_GROUP} from "../constants";

const INVOICE_TYPE = ["Normal", "Proforma", "Copy", "Training", "Advance"];
export const TRANSACTION_TYPE = ["Sale", "Refund"];
const PAYMENT_TYPE = ["Other", "Cash", "Card", "Check", "WireTransfer", "Voucher", "MobileMoney"];

export function parseInvoice(invoice, formatSdcDate, timestamp) {
    let payments = [];
    if (invoice.paymentMethod !== undefined) {
        invoice.paymentMethod.forEach(value => {
            payments.push({
                paymentType: PAYMENT_TYPE[parseInt(value.paymentType)],
                amount: value.amount
            })
        })
    }
    let transactionType = TRANSACTION_TYPE[parseInt(invoice.transactionType)];
    let invoiceType = INVOICE_TYPE[parseInt(invoice.invoiceType)];
    let sdcDateTime = invoice.sdcDateTime;
    if (formatSdcDate) {
        let date = invoice.sdcDateTime.substring(0, 19);
        sdcDateTime = date.replace("T", " ");
    }
    return {
        ...invoice,
        timestamp: timestamp,
        paymentMethod: payments,
        transactionType: transactionType,
        invoiceType: invoiceType,
        sdcDateTime: sdcDateTime
    };
}

export async function getInvoicesByUserForPeriod(arr, resolve, reject, period) {
    const startDate = period.startDate;
    const invoice = period.foundInvoice;

    if (!startDate || !AUTH.currentUser) {
        return reject();
    }

    let lastDocument = null;
    let hasMoreData = true;

    const batchSize = 50;

    while (hasMoreData) {
        try {
            let q;

            if (lastDocument) {
                q = query(
                    collection(DB, `invoices/users/${AUTH.currentUser.uid}`),
                    orderBy("sdcDateTime"),
                    startAfter(lastDocument),
                    limit(batchSize)
                );
            } else {
                q = query(
                    collection(DB, `invoices/users/${AUTH.currentUser.uid}`),
                    orderBy("sdcDateTime"),
                    startAfter(moment(startDate).format('YYYY-MM-DD')),
                    limit(batchSize)
                );
            }

            const querySnapshot = await getDocs(q);
            const snapshotSize = querySnapshot.docs.length;

            if (snapshotSize === 0) {
                resolve(arr);
                break;
            }

            for (const doc of querySnapshot.docs) {
                const parsedInvoice = parseInvoice(doc.data(), false, doc.id);
                if (parsedInvoice.invoiceNumber !== invoice.invoiceNumber) {
                    arr.push(parsedInvoice);
                    await putInvoiceOffline({
                        ...parsedInvoice,
                        isInsertedOffline: false
                    });
                } else {
                    hasMoreData = false;
                    resolve(arr);
                    break;
                }
                lastDocument = doc;
            }
        } catch (e) {
            console.error("getInvoicesByUserForPeriod", e);
            storeErrorToOffline("getInvoicesByUserForPeriod", "firestore.js", [], e?.toString());
            reject(e);
            break;
        }
    }
}


export async function getInvoicesByUser(lastData, arr, resolve, reject, fDayOfPrevMonth) {
    if (!AUTH.currentUser) {
        return reject();
    }

    if (lastData === null) {
        lastData = await getLastInsertedOfflineInvoice();
    }

    try {
        let q;
        if (!lastData) {
            q = await query(
                collection(DB, `invoices/users/${AUTH.currentUser.uid}`),
                orderBy("sdcDateTime"),
                startAfter(fDayOfPrevMonth),
                limit(50)
            );
        } else {
            q = await query(
                collection(DB, `invoices/users/${AUTH.currentUser.uid}`),
                orderBy("sdcDateTime"),
                startAfter(lastData.sdcDateTime),
                limit(50)
            );
        }

        const querySnapshot = await getDocs(q);
        const snapshotSize = querySnapshot.docs.length;

        if (snapshotSize === 0) {
            resolve(arr);
        }

        let i = 0;

        await querySnapshot.forEach((doc) => {
            const parsedInvoice = parseInvoice(doc.data(), false, doc.id);
            putInvoiceOffline({
                ...parsedInvoice,
                isInsertedOffline: false
            });
            arr.push(parsedInvoice);

            if (i === snapshotSize - 1) {
                getInvoicesByUser(parsedInvoice, arr, resolve, reject);
            }

            i = i + 1;
        });
    } catch (e) {
        console.error("getInvoicesByUser", e);
        storeErrorToOffline("getInvoicesByUserAndLocation", "firestore.js", [], e?.toString());
        reject(e);
    }
}

const desktopIpc = () => {
    try {
        return window.require?.("electron")?.ipcRenderer || null;
    } catch {
        return null;
    }
};

const printA4OnDesktop = async (html) => {
    const ipc = desktopIpc();
    if (!ipc) {
        return false;
    }
    try {
        const result = await ipc.invoke("print-a4", html);
        if (result && !result.success && result.failureReason !== "cancelled") {
            console.error("print-a4", result.failureReason);
        }
        return true;
    } catch (e) {
        console.warn("print-a4 nije dostupan, štampa ide kroz prozor", e);
        return false;
    }
};

export const printHTML = async (html, printA4 = false) => {
    if (printA4 && await printA4OnDesktop(html)) {
        return;
    }
    // 1) Pokušaj popup
    let w = Window || null;
    try {
        w = window.open(`about:blank?a4=${printA4}`, "_blank", "noopener=false,noreferrer=false");
    } catch (_) {
        w = null;
    }

    if (w) {
        // --- POPUP PUT ---
        return new Promise((resolve, reject) => {
            try {
                const done = () => {
                    try {
                        // Neki browseri ne emituju afterprint pouzdano – fallback timer
                        setTimeout(() => resolve(), 1500);
                    } catch {
                        resolve();
                    }
                };

                const onAfterPrint = () => {
                    try { w?.removeEventListener?.("afterprint", onAfterPrint); } catch {}
                    resolve();
                    // Ako želiš: w?.close();
                };

                // 1) Upis HTML-a
                const doc = w.document;
                doc.open();
                doc.write(html);
                doc.close();

                // 2) Sačekaj da se dokument zaista učita
                const waitReady = () =>
                    new Promise((res) => {
                        if (doc.readyState === "complete") return res();
                        const handle = () => {
                            if (doc.readyState === "complete") {
                                doc.removeEventListener?.("readystatechange", handle);
                                res();
                            }
                        };
                        doc.addEventListener?.("readystatechange", handle);
                        // fallback polling ako event ne okine
                        const id = setInterval(() => {
                            if (doc.readyState === "complete") {
                                clearInterval(id);
                                res();
                            }
                        }, 50);
                    });

                waitReady()
                    .then(() => {
                        try {
                            w.addEventListener?.("afterprint", onAfterPrint);
                        } catch { /* noop */ }

                        // 3) Štampa
                        w.focus();
                        w.print();

                        // 4) Fallback ako afterprint ne okine
                        setTimeout(done, 2000);
                    })
                    .catch(reject);
            } catch (err) {
                reject(err);
            }
        });
    }

    // --- IFREME FALLBACK PUT (bez popupa) ---
    return new Promise((resolve, reject) => {
        try {
            const iframe = document.createElement("iframe");
            iframe.style.position = "fixed";
            iframe.style.right = "0";
            iframe.style.bottom = "0";
            iframe.style.width = "0";
            iframe.style.height = "0";
            iframe.style.border = "0";
            document.body.appendChild(iframe);

            const cleanup = () => {
                try { iframe.parentNode?.removeChild(iframe); } catch {}
            };

            iframe.addEventListener("load", () => {
                try {
                    const doc = iframe.contentDocument;
                    const win = iframe.contentWindow;
                    if (!doc || !win) throw new Error("Print iframe not ready");

                    doc.open();
                    doc.write(html);
                    doc.close();

                    const onAfterPrint = () => {
                        try { win.removeEventListener?.("afterprint", onAfterPrint); } catch {}
                        cleanup();
                        resolve();
                    };
                    try { win.addEventListener?.("afterprint", onAfterPrint); } catch {}

                    win.focus();
                    win.print();

                    setTimeout(() => {
                        cleanup();
                        resolve();
                    }, 2000);
                } catch (e) {
                    cleanup();
                    reject(e);
                }
            }, { once: true });

            // Trigeruj load
            iframe.srcdoc = "<!doctype html><html><head></head><body></body></html>";
        } catch (e) {
            reject(e);
        }
    });
};



export const getDataForPrintingTicket = (ticket, { seatSelectionEnabled, isSelfSale } = {}) => {
    let ticketId = ticket.id;
    if (!ticketId) {
        ticketId = ticket.externalTicketId
    }

    let operator = ticket?.operator?.name
    let departureStationName = ticket?.departureStation?.name
    let depLocationName = ticket?.departureStation?.location?.name
    let arrLocationName = ticket?.arrivalStation?.location?.name
    let arrivalStationName = ticket.arrivalStation.name
    let ticketType = nameOrDefault((ticket?.ticketType?.printText || ticket?.ticketType?.name), TICKET_TYPE_BASIC_NAME, true);
    let journeyDate = ticket?.journeyDate ? moment(ticket?.journeyDate).format("DD.MM.YYYY") : null
    let departureTime = ticket?.timeFromStation ? moment(ticket.timeFromStation).format("HH:mm") : null
    let price = fCurrency(`${ticket.price}`)
    let cashier = ticket.cashier
    let salesTime = moment(ticket.salesTime).format("DD.MM.YYYY HH:mm")
    let platformNumber = ticket?.platformNumber || null;
    let seat = ticket?.seatReserved;
    const effectiveNumbered = ticket.seatIsNumbered || (seatSelectionEnabled && isSelfSale);
    if (!effectiveNumbered || ticket.isReturnTicket) {
        seat = ticket.isReturnTicket ? '' : 'NN';
    }
    let passengerDetails = getPassengerDetails(ticket?.passenger);
    let passengerDocumentNumber = ticket?.passenger?.passengerDocumentNumber || "";
    let outboundTicketId = ticket?.outboundTicketId
    let issuedBy = ticket?.issued?.name

    return {
        ticketId,
        operator,
        departureStationName,
        arrivalStationName,
        ticketType,
        journeyDate,
        departureTime,
        price,
        cashier,
        salesTime,
        platformNumber,
        seat,
        passengerDetails,
        passengerDocumentNumber,
        outboundTicketId,
        issuedBy,
        depLocationName,
        arrLocationName
    }
}

const getPassengerDetails = (passenger) => {
    return `${(passenger?.passengerName || "").length > 0 ? passenger?.passengerName : ""} ${(passenger?.passengerLastName || "").length > 0 ? passenger?.passengerLastName : ""}`
}

export const isPovlasticaTicket = (ticket) =>
    ticket?.ticketType?.id !== 1 && ticket?.ticketType?.operator === null;

const CANNOT_EDIT = [OPERATORS_GROUP.operator, OPERATORS_GROUP.informer]
export const disableDispatcherEdit = (row, operator) => {
    if(!row?.tDepartureTime || !row?.tArrivalTime) return false;
    if(CANNOT_EDIT.includes(Number(operator?.group))) return true;
    const current = moment();
    const parsedDeparture = moment.utc(row.tDepartureTime || row.tArrivalTime);
    if(Number(operator?.group) === OPERATORS_GROUP.dispatcher){
        return current.diff(parsedDeparture, "hours") > 24
    }
    const dayOfMonth = current.date();
    if (dayOfMonth <= 10) {
        const currentMonth = current.month();
        const departureMonth = parsedDeparture.month();

        if (departureMonth >= currentMonth - 1) {
            return false;
        }
    }
    return true;
}

export const disableDispatcherDelete = (row, operator)=>{
    if(!row?.trafficLogId) return true;
    if(!row?.tDepartureTime || !row?.tArrivalTime) return false;
    const current = moment();
    const parsedDeparture = moment.utc(row.tDepartureTime || row.tArrivalTime);
    if(Number(operator?.group) === OPERATORS_GROUP.dispatcher){
        return current.diff(parsedDeparture, "hours") > 24
    }
    const dayOfMonth = current.date();
    if (dayOfMonth <= 10) {
        const currentMonth = current.month();
        const departureMonth = parsedDeparture.month();

        if (departureMonth >= currentMonth - 1) {
            return false;
        }
    }
    return true;
}

/**
 * Da li se događaj tastature desio unutar polja za unos.
 * Koristi se da globalni keydown listeneri (Enter, strelice) ne otimaju
 * kucanje po input/textarea poljima i po DatePicker-u.
 */
export const isTypingTarget = (target) => {
    if (!target || !target.tagName) return false;
    const tag = target.tagName.toUpperCase();
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable === true;
}
